import { buildApiUrl } from "./stratoClient";

export const ANALYTICS_EVENTS = Object.freeze({
  HOMEPAGE_VIEW: "homepage_view",
  QUOTE_CTA_CLICK: "quote_cta_click",
  QUOTE_STARTED: "quote_started",
  QUOTE_STEP_COMPLETED: "quote_step_completed",
  QUOTE_UPLOAD_STARTED: "quote_upload_started",
  QUOTE_UPLOAD_COMPLETED: "quote_upload_completed",
  QUOTE_UPLOAD_FAILED: "quote_upload_failed",
  QUOTE_SUBMIT_STARTED: "quote_submit_started",
  QUOTE_SUBMITTED: "quote_submitted",
  QUOTE_SUBMIT_FAILED: "quote_submit_failed",
  STATUS_LOOKUP_STARTED: "status_lookup_started",
  STATUS_LOOKUP_SUCCESS: "status_lookup_success",
  STATUS_LOOKUP_FAILED: "status_lookup_failed",
});

const SAFE_METADATA_KEYS = new Set([
  "button",
  "errorCode",
  "extension",
  "hasFile",
  "page",
  "publicStage",
  "sizeBucket",
  "startType",
  "step",
  "stepId",
]);

export function trackEvent(eventName, metadata = {}) {
  if (!Object.values(ANALYTICS_EVENTS).includes(eventName)) return false;
  if (process.env.REACT_APP_STRATO_ANALYTICS_DISABLED === "true") return false;
  if (typeof window === "undefined") return false;

  const body = {
    eventName,
    pagePath: window.location.pathname,
    metadata: sanitizeMetadata(metadata),
  };
  const url = buildApiUrl("/public/v1/events");
  const payload = JSON.stringify(body);

  try {
    if (navigator.sendBeacon && isSameOriginUrl(url)) {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return true;
    }
  } catch {
    // Analytics must never affect customer flow.
  }

  try {
    fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

export function fileSizeBucket(sizeBytes) {
  const size = Number(sizeBytes || 0);
  if (size <= 0) return "unknown";
  if (size < 1024 * 1024) return "<1MiB";
  if (size < 10 * 1024 * 1024) return "1-10MiB";
  if (size < 25 * 1024 * 1024) return "10-25MiB";
  return "25-50MiB";
}

function sanitizeMetadata(metadata) {
  return Object.fromEntries(
    Object.entries(metadata || {})
      .filter(([key]) => SAFE_METADATA_KEYS.has(key))
      .map(([key, value]) => [key, sanitizeValue(value)])
      .filter(([, value]) => value !== undefined),
  );
}

function sanitizeValue(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") return value.slice(0, 80);
  return undefined;
}

function isSameOriginUrl(url) {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}
